import React from 'react';
import _ from 'lodash';

import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  styled
} from '@mui/material';
import {
  ResponsiveImage,
  fields
} from 'iron-forge-react-modules';
import {
  AttachMoney,
  SwapHoriz
} from '@mui/icons-material';
import {
  useForm,
  useWatch
} from 'react-hook-form';

import { FormModal } from 'modules';
import * as dataService from './dataService';
import * as types from './types';

import imgInvitationIllustration from 'assets/images/invitationIllustration/1x.png';
import imgInvitationIllustration2x from 'assets/images/invitationIllustration/2x.png';
import imgInvitationIllustration3x from 'assets/images/invitationIllustration/3x.png';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

const LeftPaneBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1)
}));

const ResultsList = styled(List)({
  paddingTop: 0,
  '& .MuiListItem-root': {
    paddingTop: 0,
    paddingBottom: 0
  }
});

const StyledSpan = styled('span')({
  color: '#E8446B',
  fontSize: 20
});



export interface IProps {
  fetchMode?: dataService.IFetchMode;

  onClose: () => void;
  onSelect: (returnAuthorizationId: string) => void;
}

export const FC: React.FC<IProps> = props => {
  const [fnQuery, tuple] = dataService.useLookUpReturnAuthorization(props.fetchMode);
  const form = useForm<types.IFormState>({
    defaultValues: {
      value: ''
    }
  });
  const [records, setRecord] = React.useState<dataService.IRecord[]>([]);



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      const records = await fnQuery(formState);
      setRecord(records);
    },
    [fnQuery]
  );



  const watch = useWatch({
    control: form.control
  });

  const debouncedProcessSubmit = React.useMemo(
    () => _.debounce(processSubmit, 1000),
    [processSubmit]
  );

  React.useEffect(
    () => {
      const value = watch.value ?? '';
      if (value.length >= 4) {
        debouncedProcessSubmit({ value });
      }
    },
    [
      debouncedProcessSubmit,
      watch.value
    ]
  );



  const loading = tuple.loading;

  return (
    <FormModal.FC
      isDoubleWide
      title="Find an RMA or associated SO"
      isLoading={loading}
      onClose={props.onClose}
      onSubmit={form.handleSubmit(processSubmit)}>
      <Grid
        container
        alignItems="center">
        <Grid
          item
          xs={12}
          md={6}>
          <LeftPaneBox>
            <Typography sx={{
              fontSize: 16,
              color: '#3A3B3D'
            }}>
              Locate the information you need to find an RMA
            </Typography>

            <Typography sx={{
              fontSize: 14,
              color: '#89898B'
            }}>
              Enter an IMEI, RMA, or Order Number
            </Typography>

            <ResponsiveImage.FC urlSet={[
              imgInvitationIllustration,
              imgInvitationIllustration2x,
              imgInvitationIllustration3x
            ]} />
          </LeftPaneBox>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}>
          <Stack spacing={1}>
            <Typography sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: 13,
              color: '#3A3B3D'
            }}>
              IMEI/RMA/Order Number <StyledSpan>*</StyledSpan>
            </Typography>

            <Typography sx={{
              fontSize: 12,
              color: '#89898B'
            }}>
              Enter any identifying information to locate the order. Results will be shown after 4 or more characters have been entered.
            </Typography>

            <ControlledTextField
              fieldKey="value"
              formControl={form.control} />

            {records.length > 0 && (
              <Typography
                sx={{
                  paddingLeft: 2,
                  marginBottom: 0
                }}
                variant="caption">
                Results
              </Typography>
            )}

            <Stack spacing={0}>
              <ResultsList>
                {_.map(records, (record, index) => (
                  <ListItem
                    disableGutters
                    key={record.id + index.toString()}>
                    <ListItemButton
                      onClick={() => props.onSelect(record.id)}>
                      <ListItemIcon>
                        {record.type === 'salesOrder' ? <AttachMoney color="primary" /> : <SwapHoriz color="primary" />}
                      </ListItemIcon>

                      <ListItemText primary={record.identifier} />
                    </ListItemButton>
                  </ListItem>
                ))}

                {_.isEmpty(records) && !tuple.loading && tuple.called && !!watch.value && (
                  <Typography>
                    No RMAs found matching that criteria.
                  </Typography>
                )}
              </ResultsList>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </FormModal.FC>
  );
};
